import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    initializeBankIDDetails,
    initializeBankIDFlow,
    checkBankIDAuthStatus,
    authenticateWithBankIDSameDevice,
    setQRModalOpen,
    setNeedsReload,
    resetBankIDFlow,
    clearQRData,
    resetBankIDState,
    setToggleReactivate,
    setInactiveAccount,
    handleReactivationSuccess as reactivationSuccessAction,
} from '../features/bankid/bankid.slice';
import {
    selectBankDetails,
    selectQRCodeData,
    selectIsInitializing,
    selectIsAuthenticating,
    selectIsQRModalOpen,
    selectQRState,
    selectIsAuthSuccessful,
    selectUserData,
    selectError,
    selectSessionTimeout,
    selectNeedsReload,
    selectIsReady,
    selectCanUseSameDevice,
    selectCanUseQRCode,
    selectToggleReactivate,
    selectInactiveAccount,
} from '../features/bankid/bankid.selectors';
import { getItem, setItem } from '../helper/localstorage.helper';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export interface UseBankIDOptions {
    mode: 'login' | 'signup';
    accountType?: 'freelancer' | 'business';
    onSuccess?: (userData: any) => void;
    onError?: (error: string) => void;
    autoInitialize?: boolean;
}

export const useBankID = (options: UseBankIDOptions) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const language = getItem('i18nextLng');    
    const successHandledRef = useRef(false);
    const bankDetails = useSelector(selectBankDetails);
    const qrCodeData = useSelector(selectQRCodeData);
    const isInitializing = useSelector(selectIsInitializing);
    const isAuthenticating = useSelector(selectIsAuthenticating);
    const isQRModalOpen = useSelector(selectIsQRModalOpen);
    const qrState = useSelector(selectQRState);
    const isAuthSuccessful = useSelector(selectIsAuthSuccessful);
    const userData = useSelector(selectUserData);
    const error = useSelector(selectError);
    const sessionTimeout = useSelector(selectSessionTimeout);
    const needsReload = useSelector(selectNeedsReload);
    const isReady = useSelector(selectIsReady);
    const canUseSameDevice = useSelector(selectCanUseSameDevice);
    const canUseQRCode = useSelector(selectCanUseQRCode);
    const toggleReactivate = useSelector(selectToggleReactivate);
    const inactiveAccount = useSelector(selectInactiveAccount);

    // Close QR modal when reactivate modal should appear
    useEffect(() => {
        if (toggleReactivate) {
            dispatch(setQRModalOpen(false));
        }
    }, [toggleReactivate, dispatch]);
    
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();


    useEffect(() => {
        if (options.autoInitialize !== false) {
            dispatch(initializeBankIDDetails() as any);
        }
    }, [dispatch, options.autoInitialize]);

    useEffect(() => {
        if (qrCodeData?.id && !isAuthSuccessful && !sessionTimeout) {
            dispatch(checkBankIDAuthStatus({
                bankIdSession: qrCodeData.id,
                mode: options.mode,
            }) as any);
            return
        }
    }, [qrCodeData?.id, isAuthSuccessful, sessionTimeout, dispatch, options.mode]);

    useEffect(() => {
        if (isAuthSuccessful && userData && options?.onSuccess && !successHandledRef.current) {
            successHandledRef.current = true;
            
            if (userData.isLoginWithBankId) {
                document.cookie = 'logged=1; path=/';
                toast.success(t('BANKID_LOGIN_SUCCESS') as string);
            }

            options.onSuccess(userData);
            dispatch(setQRModalOpen(false));

            const lang = searchParams.get("lang") || "en-us"; // fallback if missing

            const url =
                lang === "en-us"
                    ? `/dashboard/activities?lang=${lang}`
                    : `/instrumentbrada/aktiviteter?lang=${lang}`;

            navigate(url.toString());
            return
        }
    }, [isAuthSuccessful, userData, options.onSuccess, dispatch, navigate, searchParams, t]);

    // Reset success flag when auth state changes
    useEffect(() => {
        if (!isAuthSuccessful) {
            successHandledRef.current = false;
        }
    }, [isAuthSuccessful]);

    useEffect(() => {
        if (error && options.onError) {
            options.onError(error);
        }
    }, [error, options.onError]);

    const initializeQRFlow = useCallback(async () => {
        dispatch(setQRModalOpen(true))
        dispatch(initializeBankIDFlow() as any);
    }, [dispatch]);

    const authenticateSameDevice = useCallback(async () => {
        const response = await dispatch(authenticateWithBankIDSameDevice({
            mode: options.mode,
            accountType: options.accountType,
            language: language || 'sv-se',
        }) as any);
        setItem('user', JSON.stringify(response));
    }, [dispatch, options.mode, options.accountType, language]);

    const handleQRReload = useCallback(() => {
        if (!needsReload) return;
        dispatch(clearQRData());
        dispatch(initializeBankIDFlow() as any);
    }, [dispatch, needsReload]);

    const closeQRModal = useCallback(() => {
        dispatch(resetBankIDFlow());
    }, [dispatch]);

    const resetState = useCallback(() => {
        dispatch(resetBankIDState());
    }, [dispatch]);

    const getTitle = useCallback(() => {
        return options.mode === 'login'
            ? t('Login with Bank ID')
            : t('Sign up with Bank ID');
    }, [options.mode, t]);

    const handleReactivationClose = useCallback(() => {
        console.log('useBankID: handleReactivationClose called');
        dispatch(setToggleReactivate(false));
        dispatch(setInactiveAccount(false));
    }, [dispatch]);

    const handleReactivationSuccess = useCallback(() => {
        console.log('useBankID: handleReactivationSuccess called');
        dispatch(reactivationSuccessAction());
        
        if (options.autoInitialize !== false) {
            dispatch(initializeBankIDDetails() as any);
        }
    }, [dispatch, options.autoInitialize]);

    return {
        bankDetails,
        qrCodeData,
        isInitializing,
        isAuthenticating,
        isQRModalOpen,
        qrState,
        isAuthSuccessful,
        userData,
        error,
        sessionTimeout,
        needsReload,
        isReady,
        canUseSameDevice,
        canUseQRCode,
        toggleReactivate,
        inactiveAccount,
        initializeQRFlow,
        authenticateSameDevice,
        handleQRReload,
        closeQRModal,
        resetState,
        getTitle,
        language,
        handleReactivationClose,
        handleReactivationSuccess,
    };
};
